import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Calendar.css';

function Calendar({ currentDate, sessions, bookings, onDateChange, isInSeason, isDayOpen, isAdmin }) {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(currentDate.getMonth());
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());
  const [weatherData, setWeatherData] = useState({});

  // Charger la météo (Open-Meteo API — gratuit, pas de clé)
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Coordonnées d'Agon-Coutainville
        const lat = 48.997;
        const lon = -1.576;
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max,temperature_2m_min,weathercode&timezone=Europe/Paris&forecast_days=16`
        );
        const data = await response.json();
        
        if (data.daily) {
          const weather = {};
          data.daily.time.forEach((date, i) => {
            weather[date] = {
              tempMax: Math.round(data.daily.temperature_2m_max[i]),
              tempMin: Math.round(data.daily.temperature_2m_min[i]),
              code: data.daily.weathercode[i]
            };
          });
          setWeatherData(weather);
        }
      } catch (err) {
        console.log('Météo non disponible');
      }
    };
    fetchWeather();
  }, [currentMonth]);

  // Convertir le code météo en emoji
  const getWeatherEmoji = (code) => {
    if (code === undefined || code === null) return '';
    if (code <= 1) return '☀️';
    if (code <= 3) return '⛅';
    if (code <= 48) return '☁️';
    if (code <= 57) return '🌧️';
    if (code <= 67) return '🌧️';
    if (code <= 77) return '❄️';
    if (code <= 82) return '🌦️';
    if (code <= 86) return '❄️';
    if (code >= 95) return '⛈️';
    return '🌤️';
  };
  
  // Formater une date en YYYY-MM-DD sans décalage timezone
  const toDateStr = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Générer le calendrier du mois
  const generateCalendar = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const startDate = new Date(firstDay);
    // Commencer le lundi
    const dayOfWeek = startDate.getDay();
    const offset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - offset);
    
    const calendar = [];
    const totalDays = 42;
    
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = d.getMonth() === currentMonth;
      const dateStr = toDateStr(d);
      const inSeason = isInSeason(dateStr);
      const dayOpen = isDayOpen(dateStr, sessions);
      
      // Compter les réservations du jour (exclure les annulées)
      const dayBookings = bookings.filter(b => b.date === dateStr && b.status !== 'cancelled');
      const dayParticipants = dayBookings.reduce((sum, b) => sum + (b.nbParticipants || 0), 0);
      const dayRevenue = dayBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0);
      
      // Vérifier s'il y a des créneaux bloqués
      const daySessions = sessions.filter(s => s.date === dateStr);
      const hasBlocked = daySessions.some(s => s.isBlocked);
      
      calendar.push({
        date: new Date(d),
        dateStr,
        isCurrentMonth,
        inSeason,
        dayOpen,
        isToday: d.toDateString() === new Date().toDateString(),
        bookingCount: dayBookings.length,
        participantCount: dayParticipants,
        revenue: dayRevenue,
        hasBlocked
      });
    }
    
    return calendar;
  };
  
  const changeMonth = (delta) => {
    let newMonth = currentMonth + delta;
    let newYear = currentYear;
    if (newMonth < 0) { newMonth = 11; newYear--; }
    else if (newMonth > 11) { newMonth = 0; newYear++; }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };
  
  // Clic sur un jour en saison → vue journalière (même si fermé, l'admin peut l'ouvrir)
  const selectDate = (date) => {
    onDateChange(date);
    navigate(`/daily/${toDateStr(date)}`);
  };
  
  // Aller à avril de l'année en cours
  const goToCurrentSeason = () => {
    const now = new Date();
    const month = now.getMonth();
    // Si on est entre avril et octobre, aller au mois actuel
    // Sinon aller à avril de l'année prochaine
    if (month >= 3 && month <= 9) {
      setCurrentMonth(month);
      setCurrentYear(now.getFullYear());
    } else if (month < 3) {
      setCurrentMonth(3); // Avril
      setCurrentYear(now.getFullYear());
    } else {
      setCurrentMonth(3); // Avril
      setCurrentYear(now.getFullYear() + 1);
    }
  };
  
  const calendar = generateCalendar();
  const monthLabel = new Date(currentYear, currentMonth, 1)
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  
  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button onClick={() => changeMonth(-1)} className="month-nav-button">&lt;</button>
        <h2>{monthLabel}</h2>
        <button onClick={() => changeMonth(1)} className="month-nav-button">&gt;</button>
      </div>
      
      <div className="season-shortcuts">
        <button onClick={goToCurrentSeason} className="season-button">
          Aller à la saison en cours
        </button>
      </div>
      
      <div className="calendar-grid">
        <div className="calendar-days">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}
        </div>
        
        <div className="calendar-dates">
          {calendar.map((day, index) => (
            <div 
              key={index}
              onClick={() => day.inSeason ? selectDate(day.date) : null}
              className={`calendar-date ${
                day.isCurrentMonth ? 'current-month' : 'other-month'
              } ${
                day.isToday ? 'today' : ''
              } ${
                day.inSeason ? 'in-season clickable' : 'off-season'
              } ${
                day.dayOpen ? 'day-open' : ''
              }`}
            >
              <div className="date-number">
                {day.date.getDate()}
                {/* Météo — tous les jours avec prévisions */}
                {weatherData[day.dateStr] && day.isCurrentMonth && (
                  <span className="date-weather" title={`${weatherData[day.dateStr].tempMin}°/${weatherData[day.dateStr].tempMax}°`}>
                    {getWeatherEmoji(weatherData[day.dateStr].code)}
                  </span>
                )}
              </div>
              
              {/* Température */}
              {weatherData[day.dateStr] && day.isCurrentMonth && day.inSeason && (
                <div className="date-temp">
                  {weatherData[day.dateStr].tempMax}°
                </div>
              )}
              
              {/* Admin : affiche les réservations sur tous les jours en saison */}
              {day.inSeason && day.bookingCount > 0 && isAdmin && (
                <div className="date-info">
                  <span className="date-bookings">{day.bookingCount} rés.</span>
                  <span className="date-revenue">{day.revenue}€</span>
                </div>
              )}
              
              {/* Label "Fermé" seulement si pas de réservations affichées */}
              {day.inSeason && !day.dayOpen && !(isAdmin && day.bookingCount > 0) && (
                <div className="date-closed-label">Fermé</div>
              )}
              
              {day.hasBlocked && (
                <div className="date-blocked-indicator"></div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-indicator day-open"></div>
          <span>Ouvert</span>
        </div>
        <div className="legend-item">
          <div className="legend-indicator in-season-closed"></div>
          <span>En saison (fermé)</span>
        </div>
        <div className="legend-item">
          <div className="legend-indicator off-season-legend"></div>
          <span>Hors saison</span>
        </div>
        <div className="legend-item">
          <div className="legend-indicator has-blocked"></div>
          <span>Créneaux bloqués</span>
        </div>
      </div>
    </div>
  );
}

export default Calendar;
